import { storeToRefs } from "pinia"
import { GenericModelController } from "@/controllers/model-controllers/abstract/generic-model-controller"
import { WorkspaceChecklistItem } from "@/workspace/models/workspace-checklist-item.model"
import type { WorkspaceNote } from "@/workspace/models/workspace-note.model"
import type { IWorkspaceChecklistItem } from "@/workspace/interfaces/api/workspace-checklist-item.interface"
import { WorkspaceChecklistItemRepository } from "@/workspace/repositories/workspace-checklist-item-repository"
import { useWorkspaceStore } from "@/workspace/store/workspace.store"
import { useRelatedObjectsManager } from "@/composables/socket/related-objects-manager"
import { useSocketSynchronizationHandlers } from "@/composables/socket/socket-synchronization-handlers"
import type { WorkspaceNoteController } from "@/workspace/controllers/model-controllers/workspace-note-controller"
import type { IRepositoryRequestConfig } from "@/interfaces/repository/repository-request-config.interface"

/** Checklist items have their own endpoint but live inside their note, like messages inside an order. */
export class WorkspaceChecklistItemController extends GenericModelController<
  WorkspaceChecklistItem,
  IWorkspaceChecklistItem,
  WorkspaceChecklistItemRepository
> {
  constructor(workspaceNoteController: WorkspaceNoteController) {
    const workspaceStore = useWorkspaceStore()
    const { notes } = storeToRefs(workspaceStore)
    const { pushToRelatedObjects, removeFromRelatedObjects } = useRelatedObjectsManager(
      notes,
      (item: WorkspaceChecklistItem) => item.note,
      (note: WorkspaceNote) => note.id,
      (note: WorkspaceNote) => note.checklistItems,
      workspaceNoteController.retrieveMissingNote.bind(workspaceNoteController)
    )

    super(
      WorkspaceChecklistItem,
      { onCreate: pushToRelatedObjects, onDelete: removeFromRelatedObjects },
      new WorkspaceChecklistItemRepository()
    )

    this.socketSynchronizationHandlers = useSocketSynchronizationHandlers(
      WorkspaceChecklistItem,
      null,
      pushToRelatedObjects,
      removeFromRelatedObjects
    )
  }

  public async setCompletion(
    item: WorkspaceChecklistItem,
    isCompleted: boolean,
    requestConfig?: IRepositoryRequestConfig
  ): Promise<WorkspaceChecklistItem | undefined> {
    const repository = this.apiFunctions.getRepository()
    const response = isCompleted
      ? await repository.complete(item.id, requestConfig)
      : await repository.reopen(item.id, requestConfig)

    if (response.status !== 200 || !response.data?.id) return undefined
    return this.storeFunctions.updateModel(response.data)
  }
}
